import { type Control, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import type { ImportOrderFormData } from '#features/csvImport/model/ImportOrderFormValidation.ts';
import { dateFieldToJSDate } from '#features/orderForm/model/timeWindowFields.ts';
import { AddFreight } from '#features/orderForm/ui/AddFreight.tsx';
import { CustomerInput } from '#features/orderForm/ui/CustomerInput.tsx';
import { LocationForm } from '#features/orderForm/ui/LocationForm.tsx';
import { OrderTitleInput } from '#features/orderForm/ui/OrderTitleInput.tsx';

type ImportOrderDetailsProps = {
    control: Control<ImportOrderFormData>;
    index: number;
};

export const ImportOrderDetails = ({ control, index }: ImportOrderDetailsProps) => {
    const intl = useIntl();

    const { watch } = useFormContext<ImportOrderFormData>();
    const orderPrefix = `orders.${index}` as const;
    const orderNumber = watch(`${orderPrefix}.number`);
    const loadingStop = watch(`${orderPrefix}.loadingStop`);
    const loadingStartTarget = loadingStop?.startDate ?? '';

    const pickUpLabel = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.pick-up',
    });
    const deliveryLabel = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.delivery',
    });
    const orderLabel = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.orders.csv-import.order-label',
    });

    return (
        <div className='panel panel-default margin-bottom-15'>
            <div className='panel-heading display-flex align-items-center justify-content-between gap-10'>
                <div className='text-medium'>
                    {orderLabel} {index + 1}
                </div>
                {orderNumber ? <div className='text-muted'>{orderNumber}</div> : null}
            </div>
            <div className='panel-body padding-15'>
                <div className='display-grid grid-cols-3-sm column-gap-20-sm padding-bottom-10 align-items-end'>
                    <OrderTitleInput
                        intl={intl}
                        control={control}
                        orderNumberPrefix={orderPrefix}
                        showGenerateButton={false}
                    />
                    <CustomerInput intl={intl} control={control} namePrefix={orderPrefix} />
                </div>

                <div className='display-grid grid-cols-2 column-gap-20-sm align-items-end'>
                    <div className='grid-colspan-1'>
                        <div className='text-color-gray text-size-16'>{pickUpLabel}</div>
                        <LocationForm addressType={'loadingStop'} locationPrefix={orderPrefix} type={'import-modal'} />
                    </div>

                    <div className='grid-colspan-1'>
                        <div className='text-color-gray text-size-16'>{deliveryLabel}</div>
                        <LocationForm
                            addressType={'unloadingStop'}
                            locationPrefix={orderPrefix}
                            type={'import-modal'}
                            minDate={dateFieldToJSDate(loadingStartTarget)}
                        />
                    </div>
                </div>
                <div className='padding-top-10'>
                    <AddFreight state={'editOrder'} namePrefix={orderPrefix} />
                </div>
            </div>
        </div>
    );
};
